<template>
    <div id="#app" class="app-wrap">
        <!-- 登录页面单独显示，不包含布局 -->
        <router-view v-if="isLoginPage" />

        <!-- 主应用布局（包含菜单和标签页） -->
        <el-container v-else class="layout-container">
            <!-- 左侧菜单栏 -->
            <el-aside :width="isCollapsed ? '64px' : '200px'" class="sidebar">
                <div class="logo-container">
                    <h2 v-show="!isCollapsed" class="logo-title">
                        旅游管理系统
                    </h2>
                    <el-icon v-show="isCollapsed" class="logo-icon">
                        <HomeFilled />
                    </el-icon>
                </div>
                <el-menu
                    :default-active="activeMenu"
                    class="sidebar-menu"
                    background-color="rgb(48, 65, 86)"
                    text-color="#b8c7ce"
                    active-text-color="#ffffff"
                    :unique-opened="true"
                    :collapse="isCollapsed"
                    router>
                    <el-menu-item
                        index="/index"
                        @click="addTab('/index', '首页')">
                        <el-icon><HomeFilled /></el-icon>
                        <span>首页</span>
                    </el-menu-item>
                    <el-menu-item
                        index="/productManagement"
                        @click="addTab('/productManagement', '产品管理')">
                        <el-icon><Document /></el-icon>
                        <span>产品管理</span>
                    </el-menu-item>
                    <el-menu-item
                        index="/orderManagement"
                        @click="addTab('/orderManagement', '订单管理')">
                        <el-icon><Document /></el-icon>
                        <span>订单管理</span>
                    </el-menu-item>
                    <el-menu-item
                        index="/travelManagement"
                        @click="addTab('/travelManagement', '出行管理')">
                        <el-icon><User /></el-icon>
                        <span>出行管理</span>
                    </el-menu-item>
                    <el-menu-item
                        index="/bannerManagement"
                        @click="addTab('/bannerManagement', '轮播图管理')">
                        <el-icon><Picture /></el-icon>
                        <span>轮播图管理</span>
                    </el-menu-item>
                    <el-menu-item
                        index="/accountManagement"
                        @click="addTab('/accountManagement', '账号管理')">
                        <el-icon><User /></el-icon>
                        <span>账号管理</span>
                    </el-menu-item>
                    <el-menu-item
                        index="/roleManagement"
                        @click="addTab('/roleManagement', '角色管理')">
                        <el-icon><Lock /></el-icon>
                        <span>角色管理</span>
                    </el-menu-item>
                    <el-menu-item
                        index="/dataStatistics"
                        @click="addTab('/dataStatistics', '数据统计')">
                        <el-icon><Document /></el-icon>
                        <span>数据统计</span>
                    </el-menu-item>
                    <el-menu-item
                        index="/smsTemplate"
                        @click="addTab('/smsTemplate', '短信模板管理')">
                        <el-icon><Message /></el-icon>
                        <span>短信模板管理</span>
                    </el-menu-item>
                    <el-menu-item
                        index="/settings"
                        @click="addTab('/settings', '系统设置')">
                        <el-icon><Setting /></el-icon>
                        <span>系统设置</span>
                    </el-menu-item>
                </el-menu>
                <div
                    class="collapse-toggle"
                    @click="isCollapsed = !isCollapsed">
                    <el-icon>
                        <Fold v-if="!isCollapsed" />
                        <Expand v-else />
                    </el-icon>
                </div>
            </el-aside>

            <!-- 右侧内容区 -->
            <el-container class="main-container">
                <!-- 顶部标签页和用户信息 -->
                <el-header height="50px" class="header-tabs">
                    <div class="tabs-container">
                        <!-- 已打开页面标签 -->
                        <div class="tabs-view">
                            <el-tag
                                v-for="tab in openedTabs"
                                :key="tab.path"
                                :type="activeMenu === tab.path ? '' : 'info'"
                                :effect="
                                    activeMenu === tab.path ? 'dark' : 'plain'
                                "
                                :closable="tab.path !== '/'"
                                @close="closeTab(tab.path)"
                                @click="switchTab(tab.path)"
                                class="tab-item">
                                {{ tab.name }}
                            </el-tag>
                        </div>

                        <!-- 右侧用户信息 -->
                        <div class="user-info">
                            <el-dropdown @command="handleCommand">
                                <span class="user-dropdown">
                                    <el-avatar :size="32" class="user-avatar">
                                        <el-icon><User /></el-icon>
                                    </el-avatar>
                                    <span class="user-name">
                                        {{
                                            currentUser?.realName ||
                                            currentUser?.username ||
                                            '管理员'
                                        }}
                                    </span>
                                    <el-icon class="el-icon--right">
                                        <ArrowDown />
                                    </el-icon>
                                </span>
                                <template #dropdown>
                                    <el-dropdown-menu>
                                        <el-dropdown-item command="profile">
                                            <el-icon><User /></el-icon>
                                            个人中心
                                        </el-dropdown-item>
                                        <el-dropdown-item command="password">
                                            <el-icon><Lock /></el-icon>
                                            修改密码
                                        </el-dropdown-item>
                                        <el-dropdown-item
                                            divided
                                            command="logout">
                                            <el-icon><SwitchButton /></el-icon>
                                            退出登录
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </template>
                            </el-dropdown>
                        </div>
                    </div>
                </el-header>

                <!-- 主内容区 -->
                <el-main class="app-main">
                    <router-view v-slot="{ Component }">
                        <!-- <keep-alive :max="10">
                            <component :is="Component" />
                        </keep-alive> -->
                        <component :is="Component" />
                    </router-view>
                </el-main>
            </el-container>
        </el-container>
        <!-- 修改密码弹窗 -->
        <el-dialog
            v-model="passwordDialogVisible"
            title="修改密码"
            width="420px"
            destroy-on-close>
            <el-form
                ref="passwordFormRef"
                :model="passwordForm"
                :rules="passwordRules"
                label-width="90px">
                <el-form-item label="旧密码" prop="oldPassword">
                    <el-input
                        v-model="passwordForm.oldPassword"
                        type="password"
                        placeholder="请输入旧密码"
                        show-password />
                </el-form-item>
                <el-form-item label="新密码" prop="newPassword">
                    <el-input
                        v-model="passwordForm.newPassword"
                        type="password"
                        placeholder="6-50个字符"
                        show-password />
                </el-form-item>
                <el-form-item label="确认密码" prop="confirmPassword">
                    <el-input
                        v-model="passwordForm.confirmPassword"
                        type="password"
                        placeholder="请再次输入新密码"
                        show-password />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="passwordDialogVisible = false">
                    取消
                </el-button>
                <el-button
                    type="primary"
                    :loading="passwordSubmitting"
                    @click="handleChangePassword">
                    确定
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
    HomeFilled,
    Location,
    Calendar,
    Document,
    User,
    Setting,
    ArrowDown,
    Lock,
    SwitchButton,
    Picture,
    Fold,
    Expand,
    Message,
} from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
// eslint-disable-next-line no-duplicate-imports
import type { FormInstance, FormRules } from 'element-plus';

interface TabItem {
    path: string;
    name: string;
}

// 标签页管理逻辑
const useTabsManager = (route: any, router: any) => {
    const openedTabs = ref<TabItem[]>([{ path: '/index', name: '首页' }]);
    const activeMenu = computed(() => route.path);

    // 路由名称映射
    const routeNameMap: Record<string, string> = {
        '/index': '首页',
        '/productManagement': '产品管理',
        '/productManagement/add': '产品编辑',
        '/orderManagement': '订单管理',
        '/travelManagement': '出行管理',
        '/bannerManagement': '轮播图管理',
        '/accountManagement': '账号管理',
        '/roleManagement': '角色管理',
        '/dataStatistics': '数据统计',
        '/smsTemplate': '短信模板管理',
        '/settings': '系统设置',
    };

    const addTab = (path: string, name: string) => {
        const exists = openedTabs.value.find((tab) => tab.path === path);
        if (!exists) {
            openedTabs.value.push({ path, name });
        }
    };

    const closeTab = (path: string) => {
        const index = openedTabs.value.findIndex((tab) => tab.path === path);
        if (index !== -1) {
            openedTabs.value.splice(index, 1);
            if (path === route.path && openedTabs.value.length > 0) {
                const targetTab = openedTabs.value[Math.max(0, index - 1)];
                router.push(targetTab.path);
            }
        }
    };

    const switchTab = (path: string) => {
        router.push(path);
    };

    // 菜单级路由（只有这些路径才自动添加标签页）
    const menuPaths = new Set([
        '/index',
        '/productManagement',
        '/orderManagement',
        '/travelManagement',
        '/bannerManagement',
        '/accountManagement',
        '/roleManagement',
        '/dataStatistics',
        '/smsTemplate',
        '/settings',
    ]);

    // 监听路由变化，自动添加标签页（仅菜单级页面）
    watch(
        () => route.path,
        (newPath) => {
            if (newPath === '/' || !menuPaths.has(newPath)) return;
            const tabName = routeNameMap[newPath] || '未命名页面';
            addTab(newPath, tabName);
        },
        { immediate: true },
    );

    return { openedTabs, activeMenu, addTab, closeTab, switchTab };
};

// 用户操作逻辑（含修改密码）
const useUserActions = (router: any) => {
    const passwordDialogVisible = ref(false);
    const passwordSubmitting = ref(false);
    const passwordFormRef = ref<FormInstance>();
    const passwordForm = ref({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const passwordRules: FormRules = {
        oldPassword: [
            { required: true, message: '请输入旧密码', trigger: 'blur' },
        ],
        newPassword: [
            { required: true, message: '请输入新密码', trigger: 'blur' },
            { min: 6, max: 50, message: '6-50个字符', trigger: 'blur' },
        ],
        confirmPassword: [
            { required: true, message: '请确认新密码', trigger: 'blur' },
            {
                validator: (
                    _rule: any,
                    value: string,
                    callback: (error?: Error) => void,
                ) => {
                    if (value !== passwordForm.value.newPassword) {
                        callback(new Error('两次密码不一致'));
                    } else {
                        callback();
                    }
                },
                trigger: 'blur',
            },
        ],
    };

    const handleChangePassword = async () => {
        if (!passwordFormRef.value) return;
        await passwordFormRef.value.validate();
        passwordSubmitting.value = true;
        try {
            // eslint-disable-next-line prettier/prettier
            const { changePassword } =
                await import('@/views/accountManagement/api');
            await changePassword({
                oldPassword: passwordForm.value.oldPassword,
                newPassword: passwordForm.value.newPassword,
            });
            ElMessage.success('密码修改成功，请重新登录');
            passwordDialogVisible.value = false;
            // 清除登录状态并跳转到登录页
            localStorage.removeItem('token');
            localStorage.removeItem('userInfo');
            router.push('/login');
        } catch (err: any) {
            ElMessage.error(err.message || '密码修改失败');
        } finally {
            passwordSubmitting.value = false;
        }
    };

    const handleCommand = (command: string) => {
        switch (command) {
            case 'profile':
                ElMessage.info('个人中心功能开发中...');
                break;
            case 'password':
                passwordForm.value = {
                    oldPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                };
                passwordDialogVisible.value = true;
                break;
            case 'logout':
                ElMessageBox.confirm('确定要退出登录吗？', '退出确认', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning',
                })
                    .then(async () => {
                        const { logout } = await import('@/api/auth');
                        logout();
                        ElMessage.success('退出登录成功');
                        router.push('/login');
                    })
                    .catch(() => {
                        // 用户取消
                    });
                break;
        }
    };

    return {
        handleCommand,
        passwordDialogVisible,
        passwordSubmitting,
        passwordFormRef,
        passwordForm,
        passwordRules,
        handleChangePassword,
    };
};

export default defineComponent({
    name: 'App',
    components: {
        HomeFilled,
        Location,
        Calendar,
        Document,
        User,
        Setting,
        ArrowDown,
        Lock,
        SwitchButton,
        Picture,
        Fold,
        Expand,
        Message,
    },
    setup() {
        const route = useRoute();
        const router = useRouter();

        // 判断是否为登录页
        const isLoginPage = computed(() => route.path === '/login');

        // 侧边栏折叠状态
        const isCollapsed = ref(false);

        // 获取当前用户信息
        const currentUser = computed(() => {
            const userInfoStr = localStorage.getItem('userInfo');
            if (userInfoStr) {
                try {
                    return JSON.parse(userInfoStr);
                } catch {
                    return null;
                }
            }
            return null;
        });

        const { openedTabs, activeMenu, addTab, closeTab, switchTab } =
            useTabsManager(route, router);
        const {
            handleCommand,
            passwordDialogVisible,
            passwordSubmitting,
            passwordFormRef,
            passwordForm,
            passwordRules,
            handleChangePassword,
        } = useUserActions(router);

        onMounted(() => {
            const { location } = window;
            if (location.href.indexOf('http://') > -1) {
                location.replace(location.href.replace('http://', 'https://'));
            }
        });

        return {
            isLoginPage,
            isCollapsed,
            activeMenu,
            openedTabs,
            addTab,
            closeTab,
            switchTab,
            handleCommand,
            currentUser,
            passwordDialogVisible,
            passwordSubmitting,
            passwordFormRef,
            passwordForm,
            passwordRules,
            handleChangePassword,
        };
    },
});
</script>

<style lang="less" scoped>
.app-wrap {
    height: 100vh;
    overflow: hidden;
}

.layout-container {
    height: 100%;
}

/* 左侧菜单样式 */
.sidebar {
    background-color: rgb(48, 65, 86);
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 1001;
    transition: width 0.3s ease;
    display: flex;
    flex-direction: column;
}

.logo-container {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.1),
        rgba(255, 255, 255, 0.05)
    );
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo-title {
    color: #ffffff;
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    letter-spacing: 2px;
    white-space: nowrap;
}

.logo-icon {
    color: #ffffff;
    font-size: 24px;
}

.sidebar-menu {
    border: none;
    flex: 1;
    overflow-y: auto;
}

.collapse-toggle {
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #b8c7ce;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    transition: background-color 0.3s;
    flex-shrink: 0;
}

.collapse-toggle:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: #ffffff;
}

.sidebar-menu :deep(.el-menu-item),
.sidebar-menu :deep(.el-sub-menu__title) {
    height: 50px;
    line-height: 50px;
    transition: all 0.3s;
}

.sidebar-menu :deep(.el-menu-item:hover),
.sidebar-menu :deep(.el-sub-menu__title:hover) {
    background-color: rgba(255, 255, 255, 0.1) !important;
}

.sidebar-menu :deep(.el-menu-item.is-active) {
    background: linear-gradient(90deg, #409eff, #66b1ff) !important;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
    position: relative;
}

.sidebar-menu :deep(.el-menu-item.is-active::before) {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background-color: #ffffff;
}

/* 顶部标签页样式 */
.main-container {
    background-color: #f0f2f5;
}

.header-tabs {
    background-color: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 0 20px;
    display: flex;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 1000;
}

.tabs-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 100%;
}

.tabs-view {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 5px 0;
}

.tabs-view::-webkit-scrollbar {
    height: 4px;
}

.tabs-view::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.tab-item {
    cursor: pointer;
    transition: all 0.3s;
    white-space: nowrap;
    font-size: 13px;
    height: 32px;
    line-height: 32px;
}

.tab-item:hover {
    opacity: 0.85;
}

/* 用户信息样式 */
.user-info {
    margin-left: 20px;
    padding-left: 20px;
    border-left: 1px solid #e4e7ed;
}

.user-dropdown {
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: all 0.3s;
    padding: 5px 10px;
    border-radius: 4px;
}

.user-dropdown:hover {
    background-color: #f5f7fa;
}

.user-avatar {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.user-name {
    margin: 0 8px;
    color: #303133;
    font-size: 14px;
    font-weight: 500;
}

/* 主内容区样式 */
.app-main {
    padding: 20px;
    background-color: #f0f2f5;
    overflow-y: auto;
}

.app-main :deep(.el-card) {
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
</style>
